import React, {useEffect, useState} from "react";
import {PhoneNumberField} from "../../components/form/PhoneNumberField.tsx";
import {FormTodo, FormTodoType} from "../../components/form/FormTodo.tsx";
import {PasswordInput} from "../../components/form/PasswordInput.tsx";

export interface StepOneFormData {
    first_name: string,
    last_name: string,
    email: string,
    password: string,
    repeat_password: string,
    phone_number: string,
}

interface Props {
    onUpdateFunc: (formData: StepOneFormData) => void
    formTodos: Array<FormTodoType>
    initialFormData?: StepOneFormData
    isSSOMode?: boolean
    defaultFormData?: StepOneFormData
    lockedEmail?: string
}

function getLegalDocuments(): Record<string, string> {
    return {
        'Privacybeleid': 'https://pocketcfo-io.notion.site/PocketCFO-privacy-policy-cookie-statement-237c3918a87e80a8a734d5d4152c58b9',
        'General Terms & Conditions': 'https://pocketcfo-io.notion.site/PocketCFO-general-terms-conditions-237c3918a87e80c8b204fbcdd94bae5f'
    }
}

export function StepOne({onUpdateFunc, formTodos, initialFormData, isSSOMode = false, defaultFormData, lockedEmail}: Props) {
    const [formData, setFormData] = useState<StepOneFormData>(() => {
        const baseFormData = (initialFormData ?? defaultFormData) as StepOneFormData
        return lockedEmail ? {...baseFormData, email: lockedEmail} : baseFormData
    })

    const isEmailLocked = isSSOMode || Boolean(lockedEmail)

    useEffect(() => {
        onUpdateFunc(formData)
    }, [formData])

    return (
        <div>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Maak een account aan</p>
            <p className="mt-4 text-base block text-gray-600 mb-12">
                Alle basisinformatie
            </p>
            <form action="#" method="POST" className="space-y-6">
                <div>
                    <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">
                        E-mailadres
                    </label>
                    <div className="mt-2">
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            autoComplete="email"
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({...prev, email: e.target.value}))}
                            disabled={isEmailLocked}
                            className={`px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:opacity-50${isEmailLocked ? ' bg-gray-50' : ''}`}
                        />
                        <FormTodo fieldName={'email'} formTodos={formTodos}/>
                    </div>
                </div>
                <div className="grow">
                    <label htmlFor="phone-input" className="block text-sm font-medium leading-6 text-gray-900">
                        Je mobiele nummer
                    </label>
                    <div className="mt-2">
                        <PhoneNumberField
                            defaultValue={formData.phone_number}
                            onChangeFunc={(value) => setFormData((prev) => ({...prev, phone_number: value}))}
                        />
                        <FormTodo fieldName={'phone_number'} formTodos={formTodos}/>

                    </div>
                </div>

                <div className="flex w-full gap-6">
                    <div className="grow">
                        <label htmlFor="firstName" className="block text-sm font-medium leading-6 text-gray-900">
                            Voornaam
                        </label>
                        <div className="mt-2">
                            <input
                                id="firstName"
                                name="firstName"
                                type="text"
                                required
                                autoComplete="given-name"
                                value={formData.first_name}
                                onChange={(e) => setFormData((prev) => ({...prev, first_name: e.target.value}))}
                                className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                            />
                            <FormTodo fieldName={'first_name'} formTodos={formTodos}/>
                        </div>
                    </div>
                    <div className="grow">
                        <label htmlFor="lastName" className="block text-sm font-medium leading-6 text-gray-900">
                            Achternaam
                        </label>
                        <div className="mt-2">
                            <input
                                id="lastName"
                                name="lastName"
                                type="text"
                                required
                                autoComplete="family-name"
                                value={formData.last_name}
                                onChange={(e) => setFormData((prev) => ({...prev, last_name: e.target.value}))}
                                className="px-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary sm:text-sm sm:leading-6"
                            />
                            <FormTodo fieldName={'last_name'} formTodos={formTodos}/>
                        </div>
                    </div>
                </div>


                {!isSSOMode && (
                    <>
                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">
                                    Wachtwoord
                                </label>
                            </div>
                            <div className="mt-2">
                                <PasswordInput
                                    id="password"
                                    name="password"
                                    required
                                    value={formData.password}
                                    onChange={(e) => setFormData((prev) => ({...prev, password: e.target.value}))}
                                    autoComplete="new-password"
                                    formTodos={formTodos}
                                    fieldName="password"
                                />
                            </div>
                        </div>

                        <div>
                            <div className="flex items-center justify-between">
                                <label htmlFor="passwordRepeat" className="block text-sm font-medium leading-6 text-gray-900">
                                    Herhaal wachtwoord
                                </label>
                            </div>
                            <div className="mt-2">
                                <PasswordInput
                                    id="passwordRepeat"
                                    name="passwordRepeat"
                                    required
                                    value={formData.repeat_password}
                                    onChange={(e) => setFormData((prev) => ({...prev, repeat_password: e.target.value}))}
                                    autoComplete="new-password"
                                    formTodos={formTodos}
                                    fieldName="repeat_password"
                                />
                            </div>
                        </div>
                    </>
                )}

                <div className="relative flex items-start">
                    <div className="flex h-6 items-center">
                        <input
                            id="candidates"
                            name="candidates"
                            type="checkbox"
                            aria-describedby="candidates-description"
                            className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                    </div>
                    <div className="ml-3 text-sm/6">
                        <label htmlFor="candidates">
                            Ik ga akkoord met het {Object.entries(getLegalDocuments()).map(([displayName, URL], index) => {
                            return (<>
                                {(index > 0 && index < Object.entries(getLegalDocuments()).length) && <span> en </span>}
                                <a href={URL} target="_blank" className="font-semibold text-primary hover:underline">{displayName}</a>
                            </>)
                        })
                        }
                        </label>

                    </div>
                </div>


            </form>
        </div>

    )
}
